/**
 * Resolve the API base URL for the current page.
 *
 * In local development every frontend runs on its own hostname (nano.localhost:3003,
 * shop.localhost:3001, admin.localhost:5173) and talks to the one local `web` process under that
 * same hostname on :8080. Cookies are scoped by host, not port, so this keeps this app's cookies
 * first-party while isolating them from the other apps' cookies. In particular admin's dev `jwt`
 * cookie on plain `localhost` used to ride along on every nano API call and replace the rescue
 * session with the admin's own user (see README, "Local hostnames"). Plain http://localhost:3003
 * keeps calling http://localhost:8080.
 *
 * The derivation only kicks in when both the page and the configured URL are plain-http
 * localhost / *.localhost origins, so deployed builds, ngrok tunnels and an explicit VITE_API_HOST
 * pointing at a remote API are returned untouched. An absent or unparseable value is returned as-is
 * so behaviour without the env var is unchanged.
 */
const DEFAULT_LOCAL_API_PORT = '8080';

function isLocalHostname(hostname: string) {
  return hostname === 'localhost' || hostname.endsWith('.localhost');
}

export function resolveWebApiUrl(
  envUrl: string | undefined,
  location: Pick<Location, 'protocol' | 'hostname'> = window.location,
): string | undefined {
  if (!envUrl) {
    return envUrl;
  }
  if (location.protocol !== 'http:' || !isLocalHostname(location.hostname)) {
    return envUrl;
  }

  let configured: URL;
  try {
    configured = new URL(envUrl);
  } catch {
    return envUrl;
  }
  if (configured.protocol !== 'http:' || !isLocalHostname(configured.hostname)) {
    return envUrl;
  }

  return `http://${location.hostname}:${configured.port || DEFAULT_LOCAL_API_PORT}`;
}
